import { useRef, useEffect } from 'react';
import { asyncTimeout } from '@utils/common';

/**
 * Simlpe helper that ensures that all provided fonts, images etc. has been
 * fully loaded before resolving.
 */
async function waitForContent(root) {
  await Promise.race([asyncTimeout(10_000), Promise.all([waitForImages(root), waitForFonts(), waitForIntegrations(root), asyncTimeout(50)])]).catch(err => {
    console.warn(err);
  });
}
async function waitForFonts() {
  await Promise.all(Array.from(document.fonts).map(font => font.load()));
}
async function waitForImages(root) {
  const images = [...root.querySelectorAll("img")];
  await Promise.all(images.map(img => new Promise(resolve => {
    img.loading = "eager";
    if (img.complete) {
      resolve();
    } else {
      img.addEventListener("load", () => resolve());
      img.addEventListener("abort", () => resolve());
      img.addEventListener("error", () => resolve());
    }
  })));
}
const subscribers = new Set();

/**
 * Some print targets have additional content that needs to be loaded before
 * triggering print.
 *
 * Adding callbacks through this hook ensures that the callback has been fully
 * resolved before continuing to layout the print page.
 */
function useAwaitBeforePrint(ref, handleWaiting) {
  const handleWaitingIfNecessary = async context => {
    const target = ref.current;

    // Ensure that we only trigger waitFor callbacks for content that is
    // actually within the current page
    if (!target || !context.root.contains(target)) {
      return;
    }
    return handleWaiting();
  };
  const stableCb = useRef(handleWaitingIfNecessary);
  stableCb.current = handleWaitingIfNecessary;
  useEffect(() => {
    subscribers.add(stableCb);
    return () => {
      subscribers.delete(stableCb);
    };
  }, []);
}
async function waitForIntegrations(root) {
  await Promise.all([...subscribers.values()].map(ref => ref.current?.({
    root
  })));
}

export { useAwaitBeforePrint, waitForContent, waitForIntegrations };
//# sourceMappingURL=util.wait-for-content.js.map
